import * as React from 'react';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LogoutIcon from '@mui/icons-material/Logout';
import { observer } from 'mobx-react-lite';
import { Payouts } from '../components/dashboard/payouts';
import { Button, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { WalletOutlined, PeopleOutline } from '@mui/icons-material';
import { ReactNode } from 'react';
import { RootStoreContext } from '../global/storeContext';
import { paths } from '../routes';
import { Transactions } from '../components/dashboard/transactions';
import { Users } from '../components/dashboard/users';
import OutboxIcon from '@mui/icons-material/Outbox';
import StorageOutlined from '@mui/icons-material/StorageOutlined';
import { Migrations } from '../components/dashboard/migrations';

const drawerWidth: number = 240;

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const tabs: { name: string; component: ReactNode; icon: ReactNode }[] = [
    {
        name: 'Users',
        component: <Users />,
        icon: <PeopleOutline />,
    },
    {
        name: 'Payouts',
        component: <Payouts />,
        icon: <OutboxIcon />,
    },
    {
        name: 'Transactions',
        component: <Transactions />,
        icon: <WalletOutlined />,
    },
    {
        name: 'Migrations',
        component: <Migrations />,
        icon: <StorageOutlined />,
    },
];

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        '& .MuiDrawer-paper': {
            position: 'relative',
            whiteSpace: 'nowrap',
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            boxSizing: 'border-box',
            ...(!open && {
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
                width: theme.spacing(7),
                [theme.breakpoints.up('sm')]: {
                    width: theme.spacing(9),
                },
            }),
        },
    }),
);

// TODO remove, this demo shouldn't need to reset the theme.
const defaultTheme = createTheme();

export const HomePage: React.FC = observer(() => {
    const [open, setOpen] = React.useState(true);
    const toggleDrawer = () => {
        setOpen(!open);
    };

    const { userStore, uiStore } = React.useContext(RootStoreContext);

    const [tabIndex, setTabIndex] = React.useState(0);

    const handleComponentChange = (index: number) => {
        setTabIndex(index);
    };

    const handleLogout = async () => {
        await userStore.logout().then(() => uiStore.goTo(paths.login));
    };

    return (
        <ThemeProvider theme={defaultTheme}>
            <Box sx={{ display: 'flex' }}>
                <CssBaseline />
                <AppBar position='absolute' open={open}>
                    <Toolbar
                        sx={{
                            pr: '24px', // keep right padding when drawer closed
                        }}
                    >
                        <IconButton
                            edge='start'
                            color='inherit'
                            aria-label='open drawer'
                            onClick={toggleDrawer}
                            sx={{
                                marginRight: '36px',
                                ...(open && { display: 'none' }),
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography
                            component='h1'
                            variant='h6'
                            color='inherit'
                            noWrap
                            sx={{ flexGrow: 1 }}
                        >
                            Dashboard
                        </Typography>
                        <Button
                            color='inherit'
                            variant='outlined'
                            onClick={handleLogout}
                            startIcon={<LogoutIcon />}
                        >
                            logout
                        </Button>
                    </Toolbar>
                </AppBar>
                <Drawer variant='permanent' open={open}>
                    <Toolbar
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            px: [1],
                        }}
                    >
                        <IconButton onClick={toggleDrawer}>
                            <ChevronLeftIcon />
                        </IconButton>
                    </Toolbar>
                    <Divider />
                    <List component='nav'>
                        {tabs.map((tab, index) => (
                            <ListItemButton
                                key={index}
                                onClick={() => handleComponentChange(index)}
                            >
                                <ListItemIcon>{tab.icon}</ListItemIcon>
                                <ListItemText primary={tab.name} />
                            </ListItemButton>
                        ))}
                    </List>
                </Drawer>
                <Box
                    component='main'
                    sx={{
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                                ? theme.palette.grey[100]
                                : theme.palette.grey[900],
                        flexGrow: 1,
                        height: '100vh',
                        overflow: 'auto',
                    }}
                >
                    <Toolbar />
                    <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
                        {tabs[tabIndex].component}
                    </Container>
                </Box>
            </Box>
        </ThemeProvider>
    );
});
